import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetSalaryDetails,
  UpdateSalaryDetails,
  GetAdvances,
  CreateAdvance,
  DeleteAdvance,
  GetPayrolls,
  GetPayrollDetail,
  GeneratePayrollPreview,
  SavePayroll,
  PayPayroll,
  DeletePayroll,
} from "../Api";
import { DEFAULT_STALE_TIME, ServiceQueryOptions, unwrapApiResponse } from "./query_helpers";

export const payrollQueryKeys = {
  salaryDetails: (employeeId: string) => ["salaryDetails", employeeId] as const,
  advances: (params?: Parameters<typeof GetAdvances>[0]) => ["advances", params] as const,
  payrolls: (params?: Parameters<typeof GetPayrolls>[0]) => ["payrolls", params] as const,
  payrollDetail: (id: string) => ["payrollDetail", id] as const,
};

export const useSalaryDetailsQuery = (employeeId: string, options?: ServiceQueryOptions) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    enabled: !!employeeId,
    ...options,
    queryKey: payrollQueryKeys.salaryDetails(employeeId),
    queryFn: async () => unwrapApiResponse(await GetSalaryDetails(employeeId)),
  });
};

export const useUpdateSalaryDetailsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof UpdateSalaryDetails>[0]) =>
      UpdateSalaryDetails(payload),
    onSuccess: (res) => {
      if (res?.success) {
        queryClient.invalidateQueries({ queryKey: ["salaryDetails"] });
      }
    },
  });
};

export const useAdvancesQuery = (
  params?: Parameters<typeof GetAdvances>[0],
  options?: ServiceQueryOptions
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: payrollQueryKeys.advances(params),
    queryFn: async () => unwrapApiResponse(await GetAdvances(params)),
  });
};

export const useCreateAdvanceMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof CreateAdvance>[0]) => CreateAdvance(payload),
    onSuccess: (res) => {
      if (res?.success) {
        queryClient.invalidateQueries({ queryKey: ["advances"] });
      }
    },
  });
};

export const useDeleteAdvanceMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => DeleteAdvance(id),
    onSuccess: (res) => {
      if (res?.success) {
        queryClient.invalidateQueries({ queryKey: ["advances"] });
      }
    },
  });
};

export const usePayrollsQuery = (
  params?: Parameters<typeof GetPayrolls>[0],
  options?: ServiceQueryOptions
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: payrollQueryKeys.payrolls(params),
    queryFn: async () => unwrapApiResponse(await GetPayrolls(params)),
  });
};

export const usePayrollDetailQuery = (id: string, options?: ServiceQueryOptions) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    enabled: !!id,
    ...options,
    queryKey: payrollQueryKeys.payrollDetail(id),
    queryFn: async () => unwrapApiResponse(await GetPayrollDetail(id)),
  });
};

export const useCalculatePayrollPreviewMutation = () => {
  return useMutation({
    mutationFn: async (params: Parameters<typeof GeneratePayrollPreview>[0]) => {
      const res = await GeneratePayrollPreview(params);
      return res;
    },
  });
};

export const useSavePayrollMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof SavePayroll>[0]) => SavePayroll(payload),
    onSuccess: (res) => {
      if (res?.success) {
        queryClient.invalidateQueries({ queryKey: ["payrolls"] });
        queryClient.invalidateQueries({ queryKey: ["advances"] });
      }
    },
  });
};

export const usePayPayrollMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof PayPayroll>[0]) => PayPayroll(payload),
    onSuccess: (res, payload) => {
      if (res?.success) {
        queryClient.invalidateQueries({ queryKey: ["payrolls"] });
        if (payload?.id) {
          queryClient.invalidateQueries({ queryKey: ["payrollDetail", payload.id] });
        }
      }
    },
  });
};

export const useDeletePayrollMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => DeletePayroll(id),
    onSuccess: (res) => {
      if (res?.success) {
        queryClient.invalidateQueries({ queryKey: ["payrolls"] });
        queryClient.invalidateQueries({ queryKey: ["advances"] });
      }
    },
  });
};
